import * as React from 'react';
import { useLocation, useRoute } from 'wouter';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import RideTagForm from './RideTagForm';
import {
  TABLE_CODEBOOKS_PARKING,
  TABLE_CODEBOOKS_RIDE,
  TABLE_COST_CENTERS,
} from '../../Services/TableService';
import { URL_CODEBOOKS } from '../../router/constants';
import PermissionsService, { PERMISSION_RIDE_TAG } from '../../Services/PermissionsService';
import TabComponent from '../TabComponent';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { CmdTable } from '../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { CUSTOMERS_KEY, REQUIRED } from '../CmdTable/constants';
import { arrayOrder, getForm, getTableSettings } from '../CmdTable/CmdTableService';
import {
  TAG_ACTIVE_KEY,
  TAG_COLOR_KEY,
  TAG_DELETED_AT_KEY,
  TAG_DETAIL_KEY,
  TAG_NAME_KEY,
  RIDE_TAG_TABLE_KEY,
  RIDE_TAG_TABLE_SETTINGS,
  RideTagTableSettingsKeys,
} from './constants';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import { TTagRecords, TTagResponse } from './types';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import useRideTag from './useRideTag';
import { customersAtom, selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';
import useAnalytics from '../../hooks/useAnalytics';
import { tabsByPermission } from './CodeBooksService';

export default function Codebooks() {
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  // const [callTable, setCallTable] = React.useState<boolean>(false);
  const [data, setData] = React.useState<any>([]);
  const [id, setId] = React.useState<number | null>(null);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const tabs = React.useState(
    tabsByPermission(
      [TABLE_CODEBOOKS_RIDE, TABLE_CODEBOOKS_PARKING, TABLE_COST_CENTERS],
      TABLE_CODEBOOKS_RIDE,
      selectedCustomers
    )
  )[0];
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_CODEBOOKS}/ride-tag/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(RIDE_TAG_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(RIDE_TAG_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(RIDE_TAG_TABLE_KEY)
  );
  const query = useUrlQueryParams();
  const customerIdUrl = query.get('customerId');
  const { columns, columnOrder, columnVisibility } = useRideTag();
  const { trackEvent } = useAnalytics();

  React.useEffect(() => {
    if (`${URL_CODEBOOKS}` === pathname) {
      navigate(`${URL_CODEBOOKS}/ride-tag`);
    }
  }, [navigate, pathname]);

  const handleClose = React.useCallback(() => {
    navigate(`${URL_CODEBOOKS}/${TABLE_CODEBOOKS_RIDE}`);
    setIsModalOpened(false);
    setData([]);
    trackEvent([`table ${TABLE_CODEBOOKS_RIDE}`, 'click', 'Close tag form']);
  }, [navigate, trackEvent]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [RIDE_TAG_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  React.useEffect(() => {
    if (pathname === `${URL_CODEBOOKS}/ride-tag`) {
      const documentTitle = `${f({ id: 'tab.ride-tag' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    }
    if (pathname === `${URL_CODEBOOKS}/ride-tag/add`) {
      setIsModalOpened(true);
      setData(data);
      setCustomerId(null);
      setId(null);
      const documentTitle = `${f({ id: 'table.add' })} - ${f({ id: 'tab.ride-tag' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      setIsModalOpened(true);
      setData(data);
      setId(Number(params.id));
      setCustomerId(Number(customerIdUrl || null));
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({ id: 'tab.ride-tag' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, navigate, pathname]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const getDataForm = async () => {
    const response = await getForm(TABLE_CODEBOOKS_RIDE, id || undefined, customerId || undefined);
    if (response) {
      setData(response);

      return response;
    }
    setData([]);
    return [];
  };

  const getSettings = async () => {
    const settings = await getTableSettings(RIDE_TAG_TABLE_KEY);
    if (settings) {
      // setTableSettingsState(settings);
      // setSortFilter({ [`${settings.sort.column}`]: settings.sort.direction });
      handleSetOrderColumns(settings.columns);
      // setIsSettingsReady(true);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['rideTagForm', id, customerId, isModalOpened],
        queryFn: () => getDataForm(),
        enabled: isModalOpened,
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: [`cmdTableSettings-${RIDE_TAG_TABLE_KEY}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingDataForm } = results[0];
  const { isLoading: isLoadingSettings } = results[1];

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`${URL_CODEBOOKS}/${TABLE_CODEBOOKS_RIDE}/add`);
    trackEvent([`table ${TABLE_CODEBOOKS_RIDE}`, 'click', 'Click add tag button']);
  };

  const isLoading = isLoadingDataForm || isLoadingSettings;

  return (
    <div>
      <div className="w-page">
        <div className="w-page-content">
          <div className="page-header">
            <h4 className="page-header__title">
              {`${f({ id: 'menu.settings' })} / ${f({ id: 'table.systemSettings' })}`}
              <OnlineHelp topic="codebooks" />
            </h4>
          </div>
          <div className="container-fluid">
            <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} />
            <section className="basic-box border-top-thin border-top-gray">
              <div className="basic-box__content">
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {/* <div> */}
                    {PermissionsService.hasAccess(
                      PERMISSION_RIDE_TAG,
                      ['create'],
                      selectedCustomers
                    ) && (
                      <a
                        href="#"
                        data-cy="table-record-addBtn"
                        title={f({ id: 'table.addBtn' })}
                        className="e-button e-button--gray"
                        data-id="addAlarm"
                        // style="margin-right: 7px;"
                        onClick={handleAddButton}
                      >
                        {f({ id: 'table.addBtn' })}
                      </a>
                    )}
                    {/* </div> */}
                  </div>
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={RIDE_TAG_TABLE_KEY}
                      // actionColumnEnabled={true}
                      tableName={RIDE_TAG_TABLE_SETTINGS}
                      sort={{ column: TAG_NAME_KEY, direction: 'desc' }}
                      translationKeys={RideTagTableSettingsKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={RIDE_TAG_TABLE_KEY} />
                    ))}
                </div>
                {isLoading && <CmdLoader inContent={true} />}
                <CmdTable<TTagResponse, TTagRecords>
                  name={RIDE_TAG_TABLE_KEY}
                  stickyColumns={[TAG_DETAIL_KEY, TAG_DELETED_AT_KEY]}
                  lockedColumns={[TAG_ACTIVE_KEY, TAG_COLOR_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
            {isModalOpened && data.length > 0 && !isLoadingDataForm && (
              <RideTagForm
                id={id}
                customerId={customerId}
                data={data}
                updateTable={updateTable}
                handleClose={handleClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
